import { FC, useEffect, useRef } from 'react';
import {
  Button,
  CheckboxGroupInput,
  DataLossPrompt,
  DateTimeInput,
  Footer,
  Panel,
  SelectInput,
  SwitchInput,
  TextField,
} from 'melp-design/components';
import { Box, Divider, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import {
  NewsSendMethod,
  newsSendMethods,
  useNew,
  useNewsCategories,
  useUpdateNew,
} from 'store/news';
import { ROUTES } from 'config';
import { useRouteParams } from 'utils/useIdParam';
import { usePredefinedToasts } from 'utils/Toast';

const EMPTY_CATEGORY = '-';

interface Values {
  name: string;
  publishedAt: string | null;
  inactiveAt: string | null;
  draft: boolean;
  pinned: boolean;
  pinnedUntil: string | null;
  allowComments: boolean;
  sendMethods: NewsSendMethod[];
  categories: string[];
}

const INITIAL_VALUES: Values = {
  name: '',
  publishedAt: null,
  inactiveAt: null,
  draft: true,
  pinned: false,
  pinnedUntil: null,
  allowComments: true,
  sendMethods: [],
  categories: [],
};

interface Props {
  onSaveSuccess: () => void;
}

export const NewSettings: FC<Props> = ({ onSaveSuccess }) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();
  const formRef = useRef<HTMLFormElement>(null);
  const { id: newId } = useRouteParams(ROUTES.news.details);

  const { data: newItem } = useNew(newId);
  const { data: categories } = useNewsCategories();

  const { mutate: updateNew, isLoading: isUpdating } = useUpdateNew();

  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    control,
    formState: { isDirty },
  } = useForm<Values>({
    defaultValues: INITIAL_VALUES,
  });

  useEffect(() => {
    if (newItem) {
      reset({
        name: newItem.name,
        publishedAt: newItem.publishedAt,
        inactiveAt: newItem.inactiveAt,
        draft: newItem.status === 'draft',
        sendMethods: newItem.sendMethods,
        pinned: newItem.pinned,
        pinnedUntil: newItem.pinnedUntil,
        allowComments: newItem.allowComments,
        categories: newItem.categories.map((category) => category.id),
      });
    }
  }, [newItem, reset]);

  const onSubmit: SubmitHandler<Values> = (values) => {
    updateNew(
      {
        newId,
        update: {
          name: values.name,
          publishedAt: values.publishedAt,
          inactiveAt: values.inactiveAt,
          draft: values.draft,
          sendMethods: values.sendMethods,
          pinned: values.pinned,
          pinnedUntil: values.pinnedUntil,
          allowComments: values.allowComments,
          categories: values.categories.filter((c) => c !== EMPTY_CATEGORY),
        },
      },
      {
        onSuccess: () => {
          predefinedToasts.success.updated();
          onSaveSuccess();
        },
      },
    );
  };

  const [publishedAt, inactiveAt, pinned] = watch([
    'publishedAt',
    'inactiveAt',
    'pinned',
  ]);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
        <Stack gap={2}>
          <Panel title={t('sidebar.companySettingsGeneralSettings')}>
            <Stack spacing={2}>
              <Controller
                name="name"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <TextField
                      label={t('common.systemName')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      required
                      ref={ref}
                    />
                  );
                }}
              />

              <Controller
                name="categories"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <SelectInput
                      label={t('benefits.benefit_type')}
                      name={name}
                      value={value[0] ?? ''}
                      onChange={(v) => onChange([v])}
                      options={[
                        { label: '-', value: EMPTY_CATEGORY },
                        ...(categories?.items.map((category) => ({
                          label: category.name,
                          value: category.id,
                        })) ?? []),
                      ]}
                      ref={ref}
                    />
                  );
                }}
              />
            </Stack>
          </Panel>

          <Panel title={t('news.feedback.label')}>
            <Stack gap={2}>
              <Controller
                name="sendMethods"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <CheckboxGroupInput
                      label={t('news.notificationsSectionTitle')}
                      items={newsSendMethods
                        .filter((method) => method !== 'none')
                        .map((method) => ({
                          label: t(`news.send_method.${method}`),
                          value: method,
                        }))}
                      name={name}
                      value={value}
                      onChange={onChange}
                      ref={ref}
                    />
                  );
                }}
              />

              <Controller
                name="allowComments"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <SwitchInput
                      label={t('news.allowComments.label')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      ref={ref}
                    />
                  );
                }}
              />
            </Stack>
          </Panel>

          <Panel title={t('news.publishing.label')}>
            <Stack gap={2}>
              <Stack direction="row" spacing={1}>
                <Controller
                  name="publishedAt"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <DateTimeInput
                        label={t('news.startDate')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        datePickerProps={{
                          minDateTime: moment(),
                          maxDateTime: moment(inactiveAt),
                        }}
                        ref={ref}
                      />
                    );
                  }}
                />

                <Divider orientation="vertical" variant="middle" flexItem />

                <Controller
                  name="inactiveAt"
                  control={control}
                  render={({ field: { name, value, onChange, ref } }) => {
                    return (
                      <DateTimeInput
                        label={t('news.endDate')}
                        name={name}
                        value={value}
                        onChange={onChange}
                        datePickerProps={{
                          minDateTime: moment(publishedAt),
                        }}
                        ref={ref}
                      />
                    );
                  }}
                />
              </Stack>

              <Controller
                name="pinned"
                control={control}
                render={({ field: { name, value, onChange, ref } }) => {
                  return (
                    <SwitchInput
                      label={t('news.pinned.label')}
                      name={name}
                      value={value}
                      onChange={onChange}
                      ref={ref}
                    />
                  );
                }}
              />

              {pinned ? (
                <Box sx={{ width: '50%' }}>
                  <Controller
                    name="pinnedUntil"
                    control={control}
                    render={({ field: { name, value, onChange, ref } }) => {
                      return (
                        <DateTimeInput
                          label={t('news.pinnedUntil.label')}
                          name={name}
                          value={value}
                          onChange={onChange}
                          ref={ref}
                        />
                      );
                    }}
                  />
                </Box>
              ) : null}
            </Stack>
          </Panel>
        </Stack>

        <Footer visible={isDirty}>
          <Stack direction="row" justifyContent="end" gap="10px">
            <Button
              label={t('common.cancel')}
              variant="neutral-outline"
              onClick={() => {
                reset(undefined, { keepDirty: false });
              }}
            />

            <Button
              label={t('common.save')}
              variant="secondary"
              type="submit"
              disabled={isUpdating}
            />

            {publishedAt && newItem?.status === 'draft' ? (
              <Button
                label={t('news.publish', {
                  publishDate: moment(publishedAt).format('l LT'),
                  interpolation: { escapeValue: false },
                })}
                variant="primary"
                onClick={() => {
                  setValue('draft', false);
                  formRef.current?.requestSubmit();
                }}
                disabled={isUpdating}
              />
            ) : null}
          </Stack>
        </Footer>
      </form>

      <DataLossPrompt when={isDirty} />
    </>
  );
};
